import React from 'react'
import Eyebrow from '../ds/Eyebrow'

/**
 * A titled group of fields in the deploy wizard's configure step.
 *
 * Replaces the accordion this step used to be, and the reason is what the step
 * now says on its own heading: "Review the build settings". Almost every field
 * here arrives pre-filled from the source, and a review you have to open five
 * drawers to perform is one nobody performs — the collapsed state hid exactly
 * the values the screen exists to show. Everything is visible; the rule and
 * the label do the grouping the disclosure arrow used to.
 *
 * `CollapsibleComponent` is untouched and still right where a section is
 * genuinely optional reading — this is a different job, so it is a different
 * component rather than a flag on that one.
 */
const FormSection = ({ title, children, infoText, badge, summary }: {
  title: string
  children: React.ReactNode
  infoText?: string
  /** A short state word — "auto-detected", "optional". Carries color, so keep
   * it to things that are true about the section rather than about a field. */
  badge?: React.ReactNode
  /** One line of plain text after the badge. Explains the badge; never a
   * second heading. */
  summary?: string
}) => (
  // On a phone each group is a card of its own — a column of hairline-ruled
  // sections inside one tall box gives no sense of where a group starts, and
  // scrolling past a rule is not the same as arriving at a card.
  <section className="rounded-container border border-navy-500 bg-navy-800 p-4 lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0 lg:pt-7 lg:first:pt-0">
    <div className="flex items-center gap-3.5 mb-4">
      <Eyebrow className="whitespace-nowrap">{title}</Eyebrow>
      {/* The (i) is gone: `infoText` is the section's own explanation and it
          reads as a line, not as something you have to hover to find.
          The rule belongs to the desktop section; a card does not need one. */}
      <span aria-hidden="true" className="hidden lg:block flex-1 h-px bg-navy-500"></span>
      {badge}
      {summary && <span className="font-courrier text-ds-115 text-navy-300 whitespace-nowrap [@media(max-width:639.98px)]:hidden">{summary}</span>}
    </div>
    {infoText && <p className="m-0 mb-4 text-ds-115 leading-[1.5] text-navy-200">{infoText}</p>}
    {children}
  </section>
)

export default FormSection
